import { readCachedUser } from "../utils/cachedUser";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, query, orderBy, onSnapshot,
  doc, updateDoc, writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { playNotifSound } from "../utils/notifSound";
import Icon from "./Icon";
import { stripEmoji } from "../utils/text";

// ระบบแจ้งเตือนแบบ in-app: กระดิ่ง + badge + dropdown
// ใช้ร่วมกันได้ทุกหน้า (อ่าน user จาก localStorage เอง ไม่ต้องส่ง props)
const css = `
  .nb-wrap{position:relative;}
  .nb-btn{width:36px;height:36px;border-radius:50%;border:1px solid var(--border);background:var(--surface);color:var(--text);font-size:1.1rem;cursor:pointer;display:flex;align-items:center;justify-content:center;position:relative;transition:border-color 0.2s;}
  .nb-btn:hover{border-color:rgba(247,71,53,0.4);}
  .nb-badge{position:absolute;top:-4px;right:-4px;background:#ff5555;color:#fff;font-size:0.55rem;font-weight:700;border-radius:100px;padding:1px 5px;min-width:16px;text-align:center;}
  .nb-dropdown{position:absolute;top:calc(100% + 8px);right:0;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);width:320px;max-width:calc(100vw - 28px);max-height:400px;overflow-y:auto;z-index:100;box-shadow:0 8px 24px rgba(0,0,0,0.3);}
  .nb-header{display:flex;justify-content:space-between;align-items:center;padding:12px 16px;border-bottom:1px solid var(--border);}
  .nb-title{font-size:0.82rem;font-weight:700;}
  .nb-clear{font-size:0.7rem;color:var(--accent);cursor:pointer;background:none;border:none;font-family:'Prompt',sans-serif;}
  .nb-item{padding:12px 16px;border-bottom:1px solid rgba(247,71,53,0.05);cursor:pointer;transition:background 0.15s;}
  .nb-item:hover{background:rgba(247,71,53,0.03);}
  .nb-item.unread{background:rgba(247,71,53,0.04);border-left:3px solid var(--accent);}
  .nb-item-title{font-size:0.8rem;font-weight:600;margin-bottom:3px;}
  .nb-item-msg{font-size:0.72rem;color:var(--text-muted);line-height:1.4;}
  .nb-item-time{font-size:0.65rem;color:#a0a7b3;margin-top:4px;}
  .nb-empty{text-align:center;padding:24px;color:var(--text-muted);font-size:0.82rem;}
`;

export default function NotificationBell() {
  const navigate = useNavigate();
  const user = readCachedUser();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const wrapRef = useRef(null);
  const firstLoad = useRef(true); // ข้ามการเล่นเสียงตอนโหลดครั้งแรก (ของเดิมที่มีอยู่แล้ว)

  // โหลด notifications real-time
  useEffect(() => {
    if (!user.uid) return;
    const q = query(
      collection(db, "users", user.uid, "notifications"),
      orderBy("createdAt", "desc")
    );
    return onSnapshot(q, (snap) => {
      setNotifs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      // มีแจ้งเตือนใหม่จริง ๆ (ไม่ใช่การโหลดครั้งแรก) → เล่นเสียง
      const hasNew = snap.docChanges().some((c) => c.type === "added");
      if (hasNew && !firstLoad.current) playNotifSound();
      firstLoad.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.uid]);

  // ปิด dropdown เมื่อคลิกนอกกล่อง
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (event) => { if (event.key === "Escape") { setOpen(false); wrapRef.current?.querySelector(".nb-btn")?.focus(); } };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, [open]);

  if (!user.uid) return null;

  const unreadCount = notifs.filter((n) => !n.read).length;

  const markAllRead = async () => {
    const unread = notifs.filter((n) => !n.read);
    if (!unread.length) return;
    const batch = writeBatch(db);
    unread.forEach((n) => {
      batch.update(doc(db, "users", user.uid, "notifications", n.id), { read: true });
    });
    try { await batch.commit(); setError(""); }
    catch (_) { setError("บันทึกสถานะไม่สำเร็จ กรุณาลองอีกครั้ง"); }
  };

  const openItem = async (n) => {
    await updateDoc(doc(db, "users", user.uid, "notifications", n.id), { read: true }).catch(() => {});
    if (n.roomId) navigate(`/chat/${n.roomId}`);
    setOpen(false);
  };

  return (
    <div className="nb-wrap" ref={wrapRef}>
      <style>{css}</style>
      <button className="nb-btn" aria-label="การแจ้งเตือน" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <Icon name="bell" size={20} />
        {unreadCount > 0 && <span className="nb-badge">{unreadCount}</span>}
      </button>
      {open && (
        <div className="nb-dropdown">
          {error && <p className="error-msg" role="alert">{error}</p>}
          <div className="nb-header">
            <span className="nb-title">การแจ้งเตือน</span>
            {unreadCount > 0 && (
              <button className="nb-clear" onClick={markAllRead}>อ่านทั้งหมด</button>
            )}
          </div>
          {notifs.length === 0 ? (
            <div className="nb-empty">ยังไม่มีการแจ้งเตือน</div>
          ) : (
            notifs.slice(0, 20).map((n) => {
              const d = n.createdAt?.toDate ? n.createdAt.toDate() : null;
              const diff = d ? Math.floor((Date.now() - d) / 60000) : null;
              const timeStr =
                diff === null ? "" :
                diff < 1 ? "เพิ่งนี้" :
                diff < 60 ? `${diff} นาทีที่แล้ว` :
                diff < 1440 ? `${Math.floor(diff / 60)} ชม.ที่แล้ว` :
                `${Math.floor(diff / 1440)} วันที่แล้ว`;
              return (
                <button type="button"
                  key={n.id}
                  className={`nb-item${!n.read ? " unread" : ""}`}
                  onClick={() => openItem(n)}
                >
                  <div className="nb-item-title">{stripEmoji(n.title)}</div>
                  <div className="nb-item-msg">{stripEmoji(n.message)}</div>
                  <div className="nb-item-time">{timeStr}</div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
